/* eslint-disable compat/compat */
/* eslint-disable @typescript-eslint/no-floating-promises */
import PortalIdParser from 'PortalIdParser';
import { _CrossTabManager } from './CrossTabManager';
import { sendCrossTabMessage } from 'worker-connection-library/calling-cross-tab-utils/sendCrossTabMessage';
import { createBroadcastMessage, createChannelBroadcastMessage } from '../messages/createBroadcastMessages';
import { DUPLICATE_POPUP_OPENED, HEARTBEAT_REQUEST, HEARTBEAT_RESPONSE, POPUP_CLOSED, POPUP_OPENED, REMOTE_OPENED, isBroadcastSdkEvent } from '../types/BroadcastEvents';
import { CALL_COMPLETED, INCOMING_CALL } from '../types/ThirdPartyToHostEvents';
import { createPollingBroadcastChannel } from '../utils/createPollingBroadcastChannel';
import { handleBroadcastSdkEvents } from '../utils/handleBroadcastSdkEvents';
import { handleCallCompletedEvent } from '../utils/handleCallCompletedEvent';
import { logDebugMessage, logDebugMessageOnce } from '../utils/logDebugMessage';
import { Metrics } from '../utils/Metrics';
export class _RemoteConnectionManager extends _CrossTabManager {
  // how often to poll.
  // how long to wait for response before disconnecting.
  // how long to wait for response before alerting the user to open the calling window.
  // calling window's connection id

  // timeout for heartbeat
  // timeout for open calling window alert

  constructor() {
    super();
    this.heartbeatFrequency = 1500;
    this.heartbeatLossTimeout = 4000;
    this.openWindowAlertTimeout = 2000;
    this.duplicatedConnectionIds = [];
    this.resetWidgetDetails = () => {};
    this.updateWidgetDetails = () => {};
    this.sendCrossTabMessageToIframe = () => {};
    this.selectedProviderName = '';
    this.hasOpenWindowAlertTimedOut = false;
    this.openRemote = () => {};
    this.name = 'remoteConnectionManager';
    this.resetDuplicatedConnectionIds = () => {
      if (this.duplicatedConnectionIds.length) {
        this.duplicatedConnectionIds = [];
        logDebugMessage(`Dequeued all duplicated connection ids`);
      }
    };
    this.resetTimeouts = () => {
      clearTimeout(this.heartbeatTimeoutId);
      clearTimeout(this.openWindowAlertTimeoutId);
      this.heartbeatTimeoutId = undefined;
      this.openWindowAlertTimeoutId = undefined;
    };
    this.start = (updateWidgetDetails, resetWidgetDetails, openRemote, selectedProviderName) => {
      sendCrossTabMessage(createBroadcastMessage({
        type: REMOTE_OPENED,
        payload: {}
      }));
      this.updateWidgetDetails = updateWidgetDetails;
      this.resetWidgetDetails = resetWidgetDetails;
      this.openRemote = openRemote;
      if (selectedProviderName) {
        this.selectedProviderName = selectedProviderName;
      }
    };
    this.stop = () => {
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = undefined;
      }
      this.resetTimeouts();
      this.updateWidgetDetails = () => {};
      this.resetWidgetDetails = () => {};
    };
    this.connect = connectionId => {
      if (!connectionId) return;
      this.connectionId = connectionId;
      this.broadcastChannel = createPollingBroadcastChannel(connectionId);
      this.broadcastChannel.onmessage = event => {
        const message = event.data;
        switch (true) {
          case isBroadcastSdkEvent(message.type):
            this.handleBroadcastSdkEvents(message);
            break;
          case message.type === HEARTBEAT_RESPONSE:
            this.handleHeartbeatResponse(message);
            break;
          default:
            break;
        }
      };
      logDebugMessage(`Connected to ${this.broadcastChannel.name}`);
    };
    this.disconnect = () => {
      if (!this.connectionId || !this.broadcastChannel) return;
      const nextConnectionId = this.duplicatedConnectionIds.pop();
      // Restart the connection with the next connection id
      if (nextConnectionId) {
        this.connect(nextConnectionId);
        sendCrossTabMessage(createBroadcastMessage({
          type: REMOTE_OPENED,
          payload: {}
        }));
        return;
      }
      logDebugMessage(`Disconnected from ${this.connectionId}`);
      this.broadcastChannel.close();
      this.broadcastChannel = undefined;
      this.connectionId = undefined;
      this.resetDuplicatedConnectionIds();
      this.resetWidgetDetails();

      // Sends a message to all remotes to disconnect the broadcast channel
      sendCrossTabMessage(createBroadcastMessage({
        type: POPUP_CLOSED
      }));
    };
    this.handleHeartbeatResponse = message => {
      this.resetTimeouts();
      logDebugMessageOnce(`Received message from Popup ${HEARTBEAT_RESPONSE}`, message);
      if (this.duplicatedConnectionIds.length) {
        this.duplicatedConnectionIds.forEach(duplicatedConnectionId => {
          if (duplicatedConnectionId !== this.connectionId) {
            sendCrossTabMessage(createBroadcastMessage({
              type: DUPLICATE_POPUP_OPENED,
              payload: {},
              destinationConnectionId: duplicatedConnectionId
            }));
          }
        });
        this.resetDuplicatedConnectionIds();
      }
      if (this.hasOpenWindowAlertTimedOut) {
        // False alarm, popup is still alive
        this.updateWidgetDetails({
          isPopupOpen: true
        });
        Metrics.counter('OPEN_WINDOW_ALERT_FALSE_ALARM', {
          provider: this.selectedProviderName
        }).increment();
        this.hasOpenWindowAlertTimedOut = false;
      }
    };
    this.handleBroadcastSdkEvents = message => {
      // Clear the heartbeat timeout as popup connection is still alive
      this.resetTimeouts();
      if (this.connectionId === message.originConnectionId) {
        logDebugMessage(`Received message from SDK in Popup ${message.type}`, message);
        handleBroadcastSdkEvents(message, this.updateWidgetDetails);
        switch (message.type) {
          case CALL_COMPLETED:
            handleCallCompletedEvent(message);
            break;
          case INCOMING_CALL:
            this.openRemote();
            break;
          default:
            break;
        }
        return;
      }
      logDebugMessage(`Received message from SDK in Duplicate Popup ${message.type}`, message);
    };
    this.showOpenWindowAlert = () => {
      this.hasOpenWindowAlertTimedOut = true;
      this.updateWidgetDetails({
        isPopupOpen: false
      });
      logDebugMessage('Show no popup exists alert');
    };
    this.heartbeat = () => {
      if (!this.broadcastChannel) {
        return;
      }
      const requestMessage = createChannelBroadcastMessage({
        type: HEARTBEAT_REQUEST,
        payload: {},
        messageRelayChannel: this.broadcastChannel.name
      });
      this.broadcastChannel.postMessage(requestMessage);
      if (!this.openWindowAlertTimeoutId && !this.hasOpenWindowAlertTimedOut) {
        this.openWindowAlertTimeoutId = setTimeout(() => this.showOpenWindowAlert(), this.openWindowAlertTimeout);
      }
      if (!this.heartbeatTimeoutId) {
        this.heartbeatTimeoutId = setTimeout(() => this.stopHeartbeat(), this.heartbeatLossTimeout);
      }
    };
    this.startHeartbeat = connectionId => {
      // Handle new connection while existing connection is still alive
      if (this.connectionId && this.connectionId !== connectionId) {
        logDebugMessage(`Enqueued duplicated connection`, connectionId);
        this.duplicatedConnectionIds.push(connectionId);
        return;
      }

      // Existing connection is still alive
      if (this.connectionId && this.heartbeatTimer) {
        this.resetTimeouts();
        return;
      }
      this.connect(connectionId);
      this.heartbeatTimer = setInterval(() => {
        this.heartbeat();
      }, this.heartbeatFrequency);
      logDebugMessage(`Start heartbeat`, connectionId);
    };
    this.stopHeartbeat = () => {
      if (this.hasOpenWindowAlertTimedOut) {
        this.hasOpenWindowAlertTimedOut = false;
      } else {
        this.showOpenWindowAlert();
      }
      this.resetTimeouts();
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
      logDebugMessage('Stop heartbeat', this.connectionId);
      this.disconnect();
    };
    this.handleCrossTabMessage = message => {
      switch (message.type) {
        case POPUP_OPENED:
          {
            this.updateWidgetDetails(Object.assign({}, message.payload, {
              isPopupOpen: true
            }));
            this.startHeartbeat(message.originConnectionId);
            logDebugMessage(`Received message from Popup ${POPUP_OPENED}`, message);
            break;
          }
        case POPUP_CLOSED:
          {
            // Already disconnected
            if (!this.connectionId) {
              return;
            }
            logDebugMessage(`Received message from Remote ${POPUP_CLOSED}`, message);
            this.updateWidgetDetails({
              isPopupOpen: false
            });
            this.stopHeartbeat();
            break;
          }
        default:
          break;
      }
    };
    this.connectionId = undefined; // Initialize window's connection id
    this.storageTabLastUpdatedKey = `Tab_Window-${PortalIdParser.get()}`;
  }
}
export const remoteConnectionManager = new _RemoteConnectionManager();